import React from 'react'
import CIcon from '@coreui/icons-react'
import {
  cilCheckCircle,
  cilXCircle,
  cilWarning,
  cilLockLocked,
  cilPencil,
  cilEnvelopeClosed,
  cilHistory,
} from '@coreui/icons'

export type StatusType =
  | 'active'
  | 'inactive'
  | 'frozen'
  | 'unknown'
  | 'draft'
  | 'published'
  | 'archived'
  | 'pendingApproval'
  | 'cancelled'
  | 'ended'
  | 'new'
  | 'actioned'
  | 'dismissed'

interface StatusIndicatorProps {
  status: StatusType
  activeLabel?: string
  inactiveLabel?: string
  frozenLabel?: string
  unknownLabel?: string
  draftLabel?: string
  publishedLabel?: string
  archivedLabel?: string
  pendingApprovalLabel?: string
  cancelledLabel?: string
  endedLabel?: string
  newLabel?: string
  actionedLabel?: string
  dismissedLabel?: string
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
  frozenLabel = 'Frozen',
  unknownLabel = 'Unknown',
  draftLabel = 'Draft',
  publishedLabel = 'Published',
  archivedLabel = 'Archived',
  pendingApprovalLabel = 'Awaiting approval',
  cancelledLabel = 'Cancelled',
  endedLabel = 'Ended',
  newLabel = 'New',
  actionedLabel = 'Actioned',
  dismissedLabel = 'Dismissed',
}) => {
  const getConfig = () => {
    switch (status) {
      case 'active':
        return {
          icon: cilCheckCircle,
          colorClass: 'text-success',
          label: activeLabel,
        }
      case 'inactive':
        return {
          icon: cilXCircle,
          colorClass: 'text-secondary',
          label: inactiveLabel,
        }
      case 'frozen':
        return {
          icon: cilLockLocked,
          colorClass: 'text-warning',
          label: frozenLabel,
        }
      case 'draft':
        return {
          icon: cilPencil,
          colorClass: 'text-secondary',
          label: draftLabel,
        }
      case 'published':
        return {
          icon: cilCheckCircle,
          colorClass: 'text-success',
          label: publishedLabel,
        }
      case 'archived':
        return {
          icon: cilLockLocked,
          colorClass: 'text-warning',
          label: archivedLabel,
        }
      case 'pendingApproval':
        return {
          icon: cilEnvelopeClosed,
          colorClass: 'text-info',
          label: pendingApprovalLabel,
        }
      // Amber, not grey: a cancelled subscription is still live and still resumable, so it wants
      // more attention than an ended one.
      case 'cancelled':
        return {
          icon: cilXCircle,
          colorClass: 'text-warning',
          label: cancelledLabel,
        }
      case 'ended':
        return {
          icon: cilHistory,
          colorClass: 'text-secondary',
          label: endedLabel,
        }
      // Customer feedback triage. New reads like pendingApproval deliberately - both mean waiting on
      // someone here, not on the customer.
      case 'new':
        return {
          icon: cilEnvelopeClosed,
          colorClass: 'text-info',
          label: newLabel,
        }
      case 'actioned':
        return {
          icon: cilCheckCircle,
          colorClass: 'text-success',
          label: actionedLabel,
        }
      case 'dismissed':
        return {
          icon: cilXCircle,
          colorClass: 'text-secondary',
          label: dismissedLabel,
        }
      default:
        return {
          icon: cilWarning,
          colorClass: 'text-warning',
          label: unknownLabel,
        }
    }
  }

  const config = getConfig()

  return (
    <span className={`d-inline-flex align-items-center ${config.colorClass}`}>
      <CIcon icon={config.icon} size="lg" className="me-2" />
      <span className="fw-semibold">{config.label}</span>
    </span>
  )
}

export default StatusIndicator
